
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, AlertCircle, RefreshCw, Shield } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { getPEOVerificationStatus } from '@/services/auth.service';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole
}) => {
  const { isAuthenticated, isLoading, user, userRole } = useAuth();
  const { 
    isSuperAdmin, 
    isLoading: isAdminLoading, 
    error: adminError, 
    retry: retryAdminCheck,
    canRetry,
    retryCount
  } = useAdminStatus();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isVerificationChecking, setIsVerificationChecking] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<string | null>(null);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [adminVerificationComplete, setAdminVerificationComplete] = useState(false);
  
  // Enable auto-logout after 30 minutes of inactivity
  useIdleTimeout();
  
  useEffect(() => {
    console.log('ProtectedRoute: Auth state', { 
      isLoading, 
      isAuthenticated, 
      userRole, 
      requiredRole,
      pathname: location.pathname,
      isSuperAdmin,
      isAdminLoading,
      adminError,
      retryCount
    });

    // Mark admin verification as complete when we have a definitive result
    if (!isAdminLoading && requiredRole === 'super_admin') {
      if (isSuperAdmin || adminError) {
        setAdminVerificationComplete(true);
      }
    }

    // Check PEO verification status if needed
    const checkPEOVerification = async () => {
      if (isAuthenticated && userRole === 'peo' && user?.id) {
        try {
          console.log('ProtectedRoute: Starting PEO verification check');
          setIsVerificationChecking(true);
          const status = await getPEOVerificationStatus(user.id);
          setVerificationStatus(status);
          console.log('ProtectedRoute: PEO verification status:', status);
        } catch (err) {
          console.error('Error checking PEO verification:', err);
          setError('Failed to check verification status');
        } finally {
          setIsVerificationChecking(false);
          setVerificationComplete(true);
        }
      } else {
        // If not a PEO user, we don't need to check verification
        setVerificationComplete(true);
      }
    };
    
    // Only run verification check when auth is ready
    if (!isLoading && isAuthenticated) {
      if (userRole === 'peo') {
        checkPEOVerification();
      } else {
        // Mark verification as complete for non-PEO users
        setVerificationComplete(true);
      }
    }
  }, [isLoading, isAuthenticated, user, userRole, location.pathname, requiredRole, isSuperAdmin, isAdminLoading, adminError, retryCount]);

  // If user is not authenticated, redirect to login
  if (!isAuthenticated && !isLoading) {
    console.log('ProtectedRoute: Redirecting to login - not authenticated');
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  
  // Show loader while checking auth or verification
  const showLoader = isLoading || isVerificationChecking || (isAuthenticated && !verificationComplete);
  
  // For super admin routes, show loader while admin status is being checked (with timeout)
  const showAdminLoader = requiredRole === 'super_admin' && !adminVerificationComplete && retryCount < 3;
  
  if (showLoader || showAdminLoader) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-background p-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-lg">
          {showAdminLoader ? 'Verifying admin credentials...' : 'Verifying authentication...'}
        </p>
        {showAdminLoader && retryCount > 0 && (
          <p className="text-sm text-muted-foreground mt-2">
            Retry attempt {retryCount}/3 - Please wait...
          </p>
        )}
        <p className="text-sm text-muted-foreground mt-2">Please wait while we check your credentials</p>
      </div>
    );
  }
  
  // If there was an error in authentication
  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-background p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Authentication Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Handle super admin access
  if (requiredRole === 'super_admin') {
    // If we have a definitive result that user is NOT admin, deny access
    if (adminVerificationComplete && !isSuperAdmin && !adminError) {
      console.log('ProtectedRoute: Access denied - not super admin');
      return <Navigate to="/dashboard" replace />;
    }
    
    // If there's an admin error but we've completed verification attempts, show safe mode
    if (adminVerificationComplete && adminError && retryCount >= 3) {
      return (
        <div className="min-h-screen bg-background">
          <div className="bg-orange-50 border-b border-orange-200 p-4">
            <Alert className="border-orange-200 bg-orange-50">
              <Shield className="h-4 w-4 text-orange-600" />
              <AlertTitle className="text-orange-800">Admin Safe Mode Active</AlertTitle>
              <AlertDescription className="text-orange-700">
                Unable to verify admin status due to connectivity issues. Limited functionality may be available.
                <Button 
                  onClick={retryAdminCheck} 
                  variant="outline" 
                  size="sm" 
                  className="ml-2 border-orange-300 text-orange-700 hover:bg-orange-100"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry Verification
                </Button>
              </AlertDescription>
            </Alert>
          </div>
          {children}
        </div>
      );
    }
    
    // If admin error but we can still retry, show error with retry options
    if (adminError && canRetry && retryCount < 3) {
      return (
        <div className="flex h-screen flex-col items-center justify-center bg-background p-4">
          <Alert variant="destructive" className="max-w-md mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Admin Verification Failed</AlertTitle>
            <AlertDescription>
              {adminError}
              {retryCount > 0 && (
                <span className="block mt-2 text-sm">
                  Attempted {retryCount} time{retryCount > 1 ? 's' : ''}
                </span>
              )}
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <Button onClick={retryAdminCheck} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry Verification ({3 - retryCount} attempts left)
            </Button>
          </div>
        </div>
      );
    }
    
    // If we're confirmed admin or have a recoverable error, allow access
    if (isSuperAdmin || (adminError && retryCount >= 3)) {
      console.log('ProtectedRoute: Admin access granted', { isSuperAdmin, hasError: !!adminError });
      return <>{children}</>;
    }
  }

  // Handle PEO role requirements
  if (requiredRole === 'peo') {
    if (userRole !== 'peo') {
      console.log('ProtectedRoute: Access denied - not PEO provider');
      return <Navigate to="/dashboard" replace />;
    }
    
    // Only redirect to the restricted page if trying to access the dashboard
    // and the verification status is not approved
    if (location.pathname === '/peo/dashboard' && verificationStatus !== 'approved') {
      console.log('ProtectedRoute: PEO not verified, redirecting to restricted page');
      return <Navigate to="/peo/restricted" replace />;
    }
  }
  
  // If all checks pass, render the protected content
  console.log('ProtectedRoute: Access granted');
  return <>{children}</>;
};

export default ProtectedRoute;
